import React, { useEffect, useState } from "react";
import DataTable from "../../common/CustomDataTable.jsx";
import SettingsFormModal from "../../common/SettingsFormModal.jsx";
import AddSource from "./AddSource.jsx";
import { Link } from "react-router-dom";
import PageTitle from "../../common/PageTitle.jsx";
import Swal from "sweetalert2";
import { toast } from '../../common/Toast/toast';
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { geturls, deleteurls } from "../../../../services/Url.js";
import { confirm } from '../../common/Confirm/confirm';

const ListSource = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  /* The add dialog. The button used to link to "/add-source", which is
     not a route -- the real one is "/setting/add-source" -- so adding a
     source landed on the 404 page. */
  const [showAdd, setShowAdd] = useState(false);

  const recordsPerPage = 10;

  /* ---------------- FETCH DATA ---------------- */
  const fetchSources = async () => {
    try {
      const response = await geturls(
        recordsPerPage,
        currentPage,
        searchText // 🔥 backend search by SOURCE
      );

      setData(Array.isArray(response?.data) ? response.data : []);
      setTotalRecords(Number(response?.pagination?.total || 0));
    } catch (error) {
      console.error("Error fetching sources:", error);
    }
  };

  /* ---------------- EFFECT ---------------- */
  useEffect(() => {
    fetchSources();
  }, [currentPage, searchText]);

  /* ---------------- DELETE ---------------- */
  const handleDelete = (id) => {
    confirm({
      title: "Delete Confirmation",
      tone: 'danger',
      text: "Are you sure you want to delete this source?",
      confirmButtonText: "Yes, Delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteurls(id);
          fetchSources();

          toast.success("Source deleted successfully.");
        } catch (error) {
          toast.error("Failed to delete source.");
        }
      }
    });
  };

  /* ---------------- STATUS BADGE ---------------- */
  const renderStatus = (status) => (
    status === "ACTIVE" ? (
      <span className="badge bg-success">Active</span>
    ) : (
      <span className="badge bg-danger">Inactive</span>
    )
  );

  /* ---------------- TABLE HEADERS ---------------- */
  const headers = [
    { title: "S-No", prop: "index" },
    { title: "Source", prop: "source", sortable: true },
    { title: "Target", prop: "target", sortable: true },
    {
      title: "Status",
      prop: "status",
      cell: (row) => renderStatus(row.status),
    },
    {
      title: "Action",
      prop: "action",
      cell: (row) => (
        <>
          <OverlayTrigger overlay={<Tooltip>View</Tooltip>}>
            <button
              className="btn btn-success btn-xs sharp me-2"
              onClick={() =>
                Swal.fire({
                  title: "Source Details",
                  html: `
                    <p><b>Source:</b> ${row.source}</p>
                    <p><b>Target:</b> ${row.target}</p>
                    <p><b>Status:</b> ${row.status}</p>
                  `,
                  icon: "info",
                })
              }
            >
              <i className="fa fa-eye" />
            </button>
          </OverlayTrigger>

          <OverlayTrigger overlay={<Tooltip>Edit</Tooltip>}>
            <Link
              to={`/add-source/${row.id}`}
              className="btn btn-primary btn-xs sharp me-2"
            >
              <i className="fa fa-pencil" />
            </Link>
          </OverlayTrigger>

          <OverlayTrigger overlay={<Tooltip>Delete</Tooltip>}>
            <button
              className="btn btn-danger btn-xs sharp"
              onClick={() => handleDelete(row.id)}
            >
              <i className="fa fa-trash" />
            </button>
          </OverlayTrigger>
        </>
      ),
    },
  ];

  /* ---------------- INDEXING ---------------- */
  const indexedData = data.map((item, index) => ({
    ...item,
    index: (currentPage - 1) * recordsPerPage + index + 1,
  }));

  return (
    <>
      <PageTitle pageTitle="Source List" />

      {/* 🔍 SEARCH */}
      <div className="mb-3 d-flex justify-content-end">
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search by Source..."
          value={searchText}
          onChange={(e) => {
            setCurrentPage(1); // reset page on search
            setSearchText(e.target.value);
          }}
        />
      </div>

      <DataTable
        data={indexedData}
        headers={headers}
        totalRecords={totalRecords}
        currentPage={currentPage}
        recordsPerPage={recordsPerPage}
        onPageChange={setCurrentPage}
        serverSide={true}
        addButtonText="Add Source"
        onAddClick={() => setShowAdd(true)}
        pageTitle="Source List"
      />

      <SettingsFormModal
        open={showAdd}
        eyebrow="New sources"
        title="Add sources"
        subtitle="Each row maps a source URL to the target it should resolve to."
        labelledBy="source-form-title"
        size="wide"
        onClose={() => setShowAdd(false)}
      >
        <AddSource
          embedded
          onCancel={() => setShowAdd(false)}
          onDone={() => {
            fetchSources();
          }}
        />
      </SettingsFormModal>
    </>
  );
};

export default ListSource;
